import {ModuleAPI} from 'springboard/engine/module_api';

import {ZodType, ZodError, z, any} from 'zod';

import {generateId} from '../utils/generate_id';
import {checkWorkspaceMembership, getCurrentUserContext, Selectors} from '../selectors/selectors';
import {AttachmentChildType, AttachmentParentType, AttributeName, AttributeParentType, DB, ProjectType, StarParentType, UserActivityName, UserActivityObjectType, UserPreferenceType, WorkspaceType} from '../kysely_types';
import {Kysely} from 'kysely';
import {WorkspaceContext} from '../kysely_module';
import {DBObjects} from '../store_types';
import {isAudioFile, isDawProjectFile, findBounceFileWithPriority} from '../../utils/file_utils';

export class Actions {
    constructor(private moduleAPI: ModuleAPI) {
    }

    private getDB = () => {
        return this.moduleAPI.deps.module.moduleRegistry.getModule('Kysely').db();
    }

    public addFileToWorkspace = createZodAction(z.object({
        name: z.string().min(1),
        fullPath: z.string().min(1),
        mimeType: z.string(),
        workspace_id: z.string().min(1),
        size: z.number().min(1),
        hash: z.string().length(40),
        lastModifiedAt: z.number().min(1).optional(),
        projectId: z.string().min(1).optional(),
        existingFileId: z.string().min(1).optional(),
        avoidCreatingProjectRevision: z.boolean().optional(),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const dbService = new DBService(db, userContext.id);

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const newFileId = generateId();

        let savedFile: DBObjects['File'] | undefined;
        if (args.existingFileId) {
            const existingFile = await dbService.files.get(args.existingFileId);
            if (!existingFile) {
                throw new Error('File not found with id ' + args.existingFileId);
            }

            if (!await checkWorkspaceMembership(this.moduleAPI, existingFile)) {
                throw new Error('Not a member of workspace');
            }

            savedFile = existingFile;
        } else if (args.projectId) {
            const project = await dbService.projects.get(args.projectId);
            if (!project) {
                throw new Error('Project not found with id ' + args.projectId);
            }

            if (!await checkWorkspaceMembership(this.moduleAPI, project)) {
                throw new Error('Not a member of workspace');
            }

            const existingFile = await db.selectFrom('Attachment')
                .innerJoin('File', 'Attachment.child_id', 'File.id')
                .where('Attachment.parent_type', '=', AttachmentParentType.Project)
                .where('Attachment.parent_id', '=', args.projectId)
                .where('Attachment.child_type', '=', AttachmentChildType.File)
                .where('File.name', '=', args.name)
                .selectAll('File')
                .executeTakeFirst();

            savedFile = existingFile;
        }

        if (!savedFile) {
            savedFile = await db.insertInto('File').values({
                id: newFileId,
                name: args.name.split('/').pop()?.split('\\').pop() || args.name,
                relative_path: args.name.replace(/\\/g, '/'),
                workspace_id: args.workspace_id,
                created_by: userContext.id,
                size: args.size,
                mime_type: args.mimeType,
                earliest_known_modified_at: args.lastModifiedAt ? new Date(args.lastModifiedAt).toISOString() : undefined,
            }).execute().then(() =>
                db.selectFrom('File').where('id', '=', newFileId).selectAll().executeTakeFirstOrThrow()
            );
        }

        const revision = await dbService.files.createRevision(savedFile!.id);

        const newRawFileId = generateId();
        const rawFile = await db.insertInto('RawFile').values({
            id: newRawFileId,
            file_id: savedFile!.id,
            full_path: args.fullPath,
            workspace_id: args.workspace_id,
            created_by: userContext.id,
            file_revision_id: revision.id,
            size: args.size,
            mime_type: args.mimeType,
            hash: args.hash,
            system: getSystemType(),
            last_modified_at: args.lastModifiedAt ? new Date(args.lastModifiedAt).toISOString() : undefined,
        }).execute().then(() =>
            db.selectFrom('RawFile').where('id', '=', newRawFileId).selectAll().executeTakeFirstOrThrow()
        );

        if (args.projectId) {
            await dbService.files.addToProject(savedFile!.id, args.projectId, args.avoidCreatingProjectRevision);
        }

        return {
            ...savedFile!,
            fileRevision: revision,
            rawFile: rawFile,
        }
    });

    public addFileToProject = createZodAction(z.object({
        file_id: z.string().min(1),
        project_id: z.string().min(1),
        avoidCreatingProjectRevision: z.boolean().optional(),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        const dbService = new DBService(db, userContext.id);

        const file = await dbService.files.get(args.file_id);
        if (!file) {
            throw new Error('File not found with id ' + args.file_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, file)) {
            throw new Error('Not a member of workspace');
        }

        const project = await dbService.projects.get(args.project_id);
        if (!project) {
            throw new Error('Project not found with id ' + args.project_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, project)) {
            throw new Error('Not a member of workspace');
        }

        await dbService.files.addToProject(args.file_id, args.project_id, args.avoidCreatingProjectRevision);
    });

    public addFilesToProject = createZodAction(z.object({
        file_ids: z.array(z.string().min(1)).min(1),
        project_id: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const dbService = new DBService(db, userContext.id);

        // Validate project exists and user has access
        const project = await dbService.projects.get(args.project_id);
        if (!project) {
            throw new Error('Project not found with id ' + args.project_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, project)) {
            throw new Error('Not a member of workspace');
        }

        // Validate all files exist and user has access
        for (const fileId of args.file_ids) {
            const file = await dbService.files.get(fileId);
            if (!file) {
                throw new Error('File not found with id ' + fileId);
            }

            if (!await checkWorkspaceMembership(this.moduleAPI, file)) {
                throw new Error('Not a member of workspace');
            }
        }

        // Add all files to project, avoiding creating project revisions for all but the last one
        for (let i = 0; i < args.file_ids.length; i++) {
            const fileId = args.file_ids[i];
            const isLastFile = i === args.file_ids.length - 1;

            // Only create project revision for the last file
            await dbService.files.addToProject(fileId, args.project_id, !isLastFile);
        }
    });

    public starObject = createZodAction(z.object({
        parent_type: z.nativeEnum(StarParentType),
        parent_id: z.string().min(1),
    }), async (args) => {
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const db = this.getDB();

        const object = await db.selectFrom(args.parent_type as 'File').select('workspace_id').where('id', '=', args.parent_id).executeTakeFirstOrThrow();
        const workspace_id = object.workspace_id;
        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const dbService = new DBService(db, userContext.id);

        const result = await dbService.stars.create({
            parent_type: args.parent_type,
            parent_id: args.parent_id,
            workspace_id: workspace_id,
        });

        return result;
    });

    public unstarObject = createZodAction(z.object({
        parent_type: z.nativeEnum(StarParentType),
        parent_id: z.string().min(1),
    }), async (args) => {
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const db = this.getDB();

        const object = await db.selectFrom(args.parent_type as 'File').select('workspace_id').where('id', '=', args.parent_id).executeTakeFirstOrThrow();
        const workspace_id = object.workspace_id;
        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const dbService = new DBService(db, userContext.id);

        await dbService.stars.delete({
            parent_type: args.parent_type,
            parent_id: args.parent_id,
        });

        return {success: true};
    });

    public addCommentToFile = createZodAction(z.object({
        file_id: z.string().min(1),
        comment_text: z.string().min(1),
    }), async (args) => {
        const {comment, attachment} = await this.addCommentToThing({
            parent_type: 'File',
            parent_id: args.file_id,
            comment_text: args.comment_text,
        }, {workspace_id: null});

        const actionHooks = this.moduleAPI.getModule('UserActionHooks');
        if (actionHooks) {
            actionHooks.hooks.handleNewFileComment({fileId: args.file_id, comment: comment});
        }

        return {comment, attachment};
    });

    public deleteFile = createZodAction(z.object({
        file_id: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const file = await db.selectFrom('File').selectAll().where('File.id', '=', args.file_id).executeTakeFirst();
        if (!file) {
            throw new Error('File not found with id ' + args.file_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, file)) {
            throw new Error('Not a member of workspace');
        }

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (userContext.id !== file.created_by) {
            throw new Error('Cannot delete file that you did not create');
        }

        const childAttachments = await db.selectFrom('Attachment').selectAll().where('Attachment.parent_type', '=', 'File').where('Attachment.parent_id', '=', args.file_id).execute();
        for (const attachment of childAttachments) {
            await db.deleteFrom(attachment.child_type as 'File').where('id', '=', attachment.child_id).execute();
            await db.deleteFrom('Attachment').where('Attachment.id', '=', attachment.id).execute();
        }

        const parentAttachments = await db.selectFrom('Attachment').selectAll().where('Attachment.child_type', '=', 'File').where('Attachment.child_id', '=', args.file_id).execute();
        for (const attachment of parentAttachments) {
            await db.deleteFrom('Attachment').where('Attachment.id', '=', attachment.id).execute();
        }

        await db.deleteFrom('FileRevision').where('FileRevision.file_id', '=', args.file_id).execute();
        await db.deleteFrom('RawFile').where('RawFile.file_id', '=', args.file_id).execute();
        await db.deleteFrom('File').where('File.id', '=', args.file_id).execute();

        await db.deleteFrom('UserActivity').where('UserActivity.object_id', '=', args.file_id).execute();
        await db.deleteFrom('Star').where('Star.parent_id', '=', args.file_id).execute();
    });

    public updateFileName = createZodAction(z.object({
        file_id: z.string().min(1),
        new_name: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const file = await db.selectFrom('File').selectAll().where('File.id', '=', args.file_id).executeTakeFirst();
        if (!file) {
            throw new Error('File not found with id ' + args.file_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, file)) {
            throw new Error('Not a member of workspace');
        }

        await db.updateTable('File').set({
            name: args.new_name,
            updated_at: new Date().toISOString(),
        }).where('File.id', '=', args.file_id).execute();
    });

    public updateProjectName = createZodAction(z.object({
        project_id: z.string().min(1),
        new_name: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const project = await db.selectFrom('Project').selectAll().where('Project.id', '=', args.project_id).executeTakeFirst();
        if (!project) {
            throw new Error('Project not found with id ' + args.project_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, project)) {
            throw new Error('Not a member of workspace');
        }

        await db.updateTable('Project').set({
            name: args.new_name,
            updated_at: new Date().toISOString(),
        }).where('Project.id', '=', args.project_id).execute();
    });

    public hideProject = createZodAction(z.object({
        project_id: z.string().min(1),
        parent_id: z.string().min(1).optional(),
    }), async (args) => {
        const db = this.getDB();
        const project = await db.selectFrom('Project').selectAll().where('Project.id', '=', args.project_id).executeTakeFirst();
        if (!project) {
            throw new Error('Project not found with id ' + args.project_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, project)) {
            throw new Error('Not a member of workspace');
        }

        const parentType = args.parent_id ? AttachmentParentType.Project : AttachmentParentType.Workspace;
        const parentId = args.parent_id || project.workspace_id;

        await db.updateTable('Attachment')
            .set({
                hidden_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            })
            .where('child_type', '=', AttachmentChildType.Project)
            .where('child_id', '=', args.project_id)
            .where('parent_type', '=', parentType)
            .where('parent_id', '=', parentId)
            .execute();
    });

    public showProject = createZodAction(z.object({
        project_id: z.string().min(1),
        parent_id: z.string().min(1).optional(),
    }), async (args) => {
        const db = this.getDB();
        const project = await db.selectFrom('Project').selectAll().where('Project.id', '=', args.project_id).executeTakeFirst();
        if (!project) {
            throw new Error('Project not found with id ' + args.project_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, project)) {
            throw new Error('Not a member of workspace');
        }

        const parentType = args.parent_id ? AttachmentParentType.Project : AttachmentParentType.Workspace;
        const parentId = args.parent_id || project.workspace_id;

        await db.updateTable('Attachment')
            .set({
                hidden_at: null,
                updated_at: new Date().toISOString(),
            })
            .where('child_type', '=', AttachmentChildType.Project)
            .where('child_id', '=', args.project_id)
            .where('parent_type', '=', parentType)
            .where('parent_id', '=', parentId)
            .execute();
    });

    public reorderProjects = createZodAction(z.object({
        workspace_id: z.string().min(1),
        project_orders: z.array(z.object({
            project_id: z.string().min(1),
            index: z.number().int().min(0),
        })).min(1),
    }), async (args) => {
        const db = this.getDB();

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('User not authenticated');
        }

        await db.transaction().execute(async (trx) => {
            for (const projectOrder of args.project_orders) {
                const existingAttachment = await trx.selectFrom('Attachment')
                    .selectAll()
                    .where('child_type', '=', AttachmentChildType.Project)
                    .where('child_id', '=', projectOrder.project_id)
                    .where('parent_type', '=', AttachmentParentType.Workspace)
                    .where('parent_id', '=', args.workspace_id)
                    .executeTakeFirst();

                if (existingAttachment) {
                    await trx.updateTable('Attachment')
                        .set({
                            index: projectOrder.index,
                            updated_at: new Date().toISOString(),
                        })
                        .where('id', '=', existingAttachment.id)
                        .execute();
                } else {
                    await trx.insertInto('Attachment')
                        .values({
                            id: generateId(),
                            child_type: AttachmentChildType.Project,
                            child_id: projectOrder.project_id,
                            parent_type: AttachmentParentType.Workspace,
                            parent_id: args.workspace_id,
                            index: projectOrder.index,
                            workspace_id: args.workspace_id,
                            created_by: userContext.id,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                        })
                        .execute();
                }
            }
        });
    });

    public reorderSubProjects = createZodAction(z.object({
        parent_project_id: z.string().min(1),
        project_orders: z.array(z.object({
            project_id: z.string().min(1),
            index: z.number().int().min(0),
        })).min(1),
    }), async (args) => {
        const db = this.getDB();

        const parentProject = await db.selectFrom('Project')
            .select('workspace_id')
            .where('id', '=', args.parent_project_id)
            .executeTakeFirst();

        if (!parentProject) {
            throw new Error('Parent project not found');
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: parentProject.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const userContext = await getCurrentUserContext(this.moduleAPI);
        if (!userContext) {
            throw new Error('User not authenticated');
        }

        await db.transaction().execute(async (trx) => {
            for (const projectOrder of args.project_orders) {
                const existingAttachment = await trx.selectFrom('Attachment')
                    .selectAll()
                    .where('child_type', '=', AttachmentChildType.Project)
                    .where('child_id', '=', projectOrder.project_id)
                    .where('parent_type', '=', AttachmentParentType.Project)
                    .where('parent_id', '=', args.parent_project_id)
                    .executeTakeFirst();

                if (existingAttachment) {
                    await trx.updateTable('Attachment')
                        .set({
                            index: projectOrder.index,
                            updated_at: new Date().toISOString(),
                        })
                        .where('id', '=', existingAttachment.id)
                        .execute();
                } else {
                    await trx.insertInto('Attachment')
                        .values({
                            id: generateId(),
                            child_type: AttachmentChildType.Project,
                            child_id: projectOrder.project_id,
                            parent_type: AttachmentParentType.Project,
                            parent_id: args.parent_project_id,
                            index: projectOrder.index,
                            workspace_id: parentProject.workspace_id,
                            created_by: userContext.id,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                        })
                        .execute();
                }
            }
        });
    });

    public updateObjectAttribute = createZodAction(z.object({
        parent_type: z.nativeEnum(AttributeParentType),
        parent_id: z.string().min(1),
        attribute: z.nativeEnum(AttributeName),
        value: z.string().nullable(),
    }), async (args) => {
        const db = this.getDB();
        const parentObject = await db.selectFrom(args.parent_type as 'File').select('workspace_id')
            .where('id', '=', args.parent_id)
            .executeTakeFirstOrThrow();

        if (!parentObject) {
            throw new Error('Parent object not found with id ' + args.parent_id);
        }

        if (!await checkWorkspaceMembership(this.moduleAPI, parentObject)) {
            throw new Error('Not a member of workspace');
        }

        const userContext = await getCurrentUserContext(this.moduleAPI);

        const attributeRows = await db.selectFrom('Attribute').selectAll()
            .where('Attribute.parent_type', '=', args.parent_type)
            .where('Attribute.parent_id', '=', args.parent_id)
            .where('Attribute.name', '=', args.attribute)
            .execute();

        if (args.value === null) {
            if (attributeRows.length > 0) {
                await db.deleteFrom('Attribute')
                    .where('Attribute.id', '=', attributeRows[0].id)
                    .execute();
            }
        } else {
            if (attributeRows.length > 0) {
                await db.updateTable('Attribute').set({
                    value: args.value,
                    updated_at: new Date().toISOString(),
                }).where('Attribute.id', '=', attributeRows[0].id).execute();
            } else {
                await db.insertInto('Attribute').values({
                    id: generateId(),
                    name: args.attribute,
                    value: args.value,
                    parent_type: args.parent_type,
                    parent_id: args.parent_id,
                    workspace_id: parentObject.workspace_id,
                    created_by: userContext.id,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                }).execute();
            }
        }
    });

    public addCommentToThing = createZodAction(z.object({
        parent_type: z.enum(['File', 'Project']),
        parent_id: z.string().min(1),
        comment_text: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        if (!args.comment_text.length) {
            throw new Error('No comment text provided');
        }

        const parentObject = await db.selectFrom(args.parent_type as 'File').select('workspace_id')
            .where('id', '=', args.parent_id)
            .executeTakeFirstOrThrow();

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: parentObject.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const newCommentId = generateId();
        const comment = await db.insertInto('Comment').values({
            id: newCommentId,
            text: args.comment_text,
            created_by: userContext.id,
            workspace_id: parentObject.workspace_id,
        }).execute().then(() =>
            db.selectFrom('Comment').where('id', '=', newCommentId).selectAll().executeTakeFirstOrThrow()
        );

        const newAttachmentId = generateId();
        const attachment = await db.insertInto('Attachment').values({
            id: newAttachmentId,
            child_id: comment.id,
            child_type: 'Comment',
            created_by: userContext.id,
            parent_id: args.parent_id,
            parent_type: args.parent_type,
            updated_at: new Date().toISOString(),
            workspace_id: parentObject.workspace_id,
        }).execute().then(() =>
            db.selectFrom('Attachment').where('id', '=', newAttachmentId).selectAll().executeTakeFirstOrThrow()
        );

        return {comment, attachment};
    });

    public replyToComment = createZodAction(z.object({
        parent_id: z.string().min(1),
        text: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        const {workspace_id} = await db.selectFrom('Comment').select(['workspace_id'])
            .where('id', '=', args.parent_id)
            .executeTakeFirstOrThrow();

        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const attachment = await db.selectFrom('Attachment')
            .selectAll()
            .where('child_id', '=', args.parent_id)
            .where('child_type', '=', 'Comment')
            .executeTakeFirstOrThrow();

        const newCommentId = generateId();
        const comment = await db.insertInto('Comment').values({
            id: newCommentId,
            text: args.text,
            created_by: userContext.id,
            workspace_id,
            updated_at: '',
            reply_parent_id: args.parent_id,
        }).execute().then(() =>
            db.selectFrom('Comment').where('id', '=', newCommentId).selectAll().executeTakeFirstOrThrow()
        );

        await db.insertInto('Attachment').values({
            id: generateId(),
            child_id: comment.id,
            child_type: 'Comment',
            created_by: userContext.id,
            parent_id: attachment.parent_id,
            parent_type: attachment.parent_type,
            updated_at: new Date().toISOString(),
            workspace_id,
        }).execute();

        return comment;
    });

    public editComment = createZodAction(z.object({
        comment_id: z.string().min(1),
        text: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        const comment = await db.selectFrom('Comment')
            .select('id')
            .select('workspace_id')
            .where('Comment.created_by', '=', userContext.id) // TODO: let's actually just skip this and do a manual comparison below. so we know the actual error that occurred for sure
            .where('Comment.id', '=', args.comment_id)
            .executeTakeFirst();

        if (!comment) {
            throw new Error('Cannot edit comment. User is not the author.');
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: comment.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        await db.updateTable('Comment')
            .set({
                text: args.text,
                updated_at: new Date().toISOString(),
            })
            .where('id', '=', args.comment_id)
            .execute();
    });

    public deleteComment = createZodAction(z.object({
        comment_id: z.string().min(1),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        const comment = await db.selectFrom('Comment')
            .select('id')
            .select('workspace_id')
            .where('Comment.created_by', '=', userContext.id)
            .where('Comment.id', '=', args.comment_id)
            .executeTakeFirst();

        if (!comment) {
            throw new Error('Cannot delete comment. User is not the author.');
        }

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: comment.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        await db.deleteFrom('Attachment')
            .where('Attachment.child_type', '=', 'Comment')
            .where('Attachment.child_id', '=', args.comment_id)
            .execute();

        await db.deleteFrom('Comment')
            .where('Comment.created_by', '=', userContext.id)
            .where('Comment.id', '=', args.comment_id)
            .execute();
    });

    public createProject = createZodAction(z.object({
        name: z.string().min(1),
        workspace_id: z.string().min(1),
        parent_id: z.string().min(1).optional(),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);

        if (!(await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id}))) {
            throw new Error('User is not a member of this workspace');
        }

        let projectAncestry: string | undefined;

        if (args.parent_id) {
            const parentProject = await db.selectFrom('Project')
                .selectAll()
                .where('id', '=', args.parent_id)
                .where('workspace_id', '=', args.workspace_id)
                .executeTakeFirst();
            if (!parentProject) {
                throw new Error('Parent project not found with id ' + args.parent_id);
            }

            projectAncestry = parentProject.project_ancestry || '';
            if (projectAncestry) {
                projectAncestry += ',';
            }
            projectAncestry += `${args.parent_id}`;
        }

        const newProjectId = generateId();
        const project = await db.insertInto('Project').values({
            id: newProjectId,
            created_by: userContext.id,
            name: args.name,
            workspace_id: args.workspace_id,
            type: ProjectType.DEFAULT,
            parent_id: args.parent_id || null,
            project_ancestry: projectAncestry || null,
        }).execute().then(() =>
            db.selectFrom('Project').where('id', '=', newProjectId).selectAll().executeTakeFirst()
        );

        await db.insertInto('Project_fts' as any).values({
            id: newProjectId,
            name: args.name,
            workspace_id: args.workspace_id,
        }).execute();

        return project!;
    });

    public createWorkspace = createZodAction(z.object({
        name: z.string().min(1),
        type: z.nativeEnum(WorkspaceType),
    }), async (args) => {
        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const workspaceType = userContext.type === 'local' ? WorkspaceType.LOCAL : args.type;

        const newWorkspaceId = generateId();
        const workspace = await db.insertInto('Workspace').values({
            id: newWorkspaceId,
            name: args.name.trim(),
            owner_id: userContext.id,
            created_by: userContext.id,
            type: workspaceType,
        }).execute().then(() =>
            db.selectFrom('Workspace').where('id', '=', newWorkspaceId).selectAll().executeTakeFirst()
        );

        if (!workspace) {
            throw new Error('Failed to create workspace');
        }

        await db.insertInto('WorkspaceMember').values({
            id: generateId(),
            created_by: userContext.id,
            user_id: userContext.id,
            workspace_id: workspace.id,
        }).execute();

        return workspace;
    });

    public createUserActivity = createZodAction(z.object({
        workspace_id: z.string().min(1),
        activity_name: z.nativeEnum(UserActivityName),
        object_type: z.nativeEnum(UserActivityObjectType),
        object_id: z.string().min(1),
    }), async (args) => {
        if (!await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('User is not a member of this workspace');
        }

        const db = this.getDB();
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const dbService = new DBService(db, userContext.id);

        const activity = await dbService.user_activity.create(args);
        return activity;
    });

    public setUserPreference = createZodAction(z.object({
        key: z.nativeEnum(UserPreferenceType),
        value: z.string(),
        workspace_id: z.string().min(1).optional(),
    }), async (args) => {
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const db = this.getDB();

        if (args.workspace_id && !await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const dbService = new DBService(db, userContext.id);
        const preference = await dbService.user_preferences.upsert({
            user_id: userContext.id,
            key: args.key,
            value: args.value,
            workspace_id: args.workspace_id,
        });
        return preference;
    });

    public getUserPreference = createZodAction(z.object({
        key: z.nativeEnum(UserPreferenceType),
        workspace_id: z.string().min(1).optional(),
    }), async (args) => {
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const db = this.getDB();

        if (args.workspace_id && !await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const dbService = new DBService(db, userContext.id);
        const preference = await dbService.user_preferences.get({
            user_id: userContext.id,
            key: args.key,
            workspace_id: args.workspace_id,
        });
        return preference;
    });

    public deleteUserPreference = createZodAction(z.object({
        key: z.nativeEnum(UserPreferenceType),
        workspace_id: z.string().min(1).optional(),
    }), async (args) => {
        const userContext = await getCurrentUserContext(this.moduleAPI);
        const db = this.getDB();

        if (args.workspace_id && !await checkWorkspaceMembership(this.moduleAPI, {workspace_id: args.workspace_id})) {
            throw new Error('Not a member of workspace');
        }

        const dbService = new DBService(db, userContext.id);
        await dbService.user_preferences.delete({
            user_id: userContext.id,
            key: args.key,
            workspace_id: args.workspace_id,
        });
        return {success: true};
    });
}

// export class Actions {
//     constructor(private moduleAPI: ModuleAPI) { }

//     public createProject = this.moduleAPI.createAction('saveProject', {}, async (project: Project) => {
//     });
// }


// export class Actions {
//     constructor(private moduleAPI: ModuleAPI) { }

//     public createProject = async (project: Project) => {
//     };
// }

export class DBService {
    constructor(private db: Kysely<DB>, private userId: string) {
    }

    private getDB = () => {
        return this.db;
    };

    files = {
        get: async (fileId: string) => {
            const db = this.getDB();
            return await db.selectFrom('File').where('id', '=', fileId).selectAll().executeTakeFirst();
        },

        addToProject: async (fileId: string, projectId: string, avoidCreatingProjectRevision?: boolean) => {
            const project = await this.projects.get(projectId);
            if (!project) {
                throw new Error('Project not found');
            }

            const file = await this.files.get(fileId);
            if (!file) {
                throw new Error('File not found');
            }

            const db = this.getDB();

            const existingProjectRevisions = await db.selectFrom('ProjectRevision')
                .where('project_id', '=', projectId)
                .orderBy('revision_num', 'desc')
                .selectAll()
                .execute();

            let newProjectRevisionId: string | null = null;

            if (!avoidCreatingProjectRevision) {
                newProjectRevisionId = generateId();
                const newProjectVersion = existingProjectRevisions.length + 1;
                await db.insertInto('ProjectRevision').values({
                    id: newProjectRevisionId,
                    created_by: this.userId,
                    revision_name: project.name + ' v' + newProjectVersion,
                    revision_num: newProjectVersion,
                    workspace_id: project.workspace_id,
                    project_id: projectId,
                }).execute().then(() =>
                    db.selectFrom('ProjectRevision').where('id', '=', newProjectRevisionId).selectAll().executeTakeFirst()
                );
            }

            const existingAttachment = await db.selectFrom('Attachment')
                .where('child_type', '=', AttachmentChildType.File)
                .where('child_id', '=', fileId)
                .where('parent_type', '=', AttachmentParentType.Project)
                .selectAll()
                .executeTakeFirst();

            const existingFileRevisions = await db.selectFrom('FileRevision')
                .where('file_id', '=', fileId)
                .selectAll()
                .orderBy('revision_num', 'desc')
                .execute();

            if (existingAttachment) {
                await db.updateTable('Attachment')
                    .set({
                        parent_id: projectId,
                        updated_at: new Date().toISOString(),
                    })
                    .where('id', '=', existingAttachment.id)
                    .execute();
            } else {
                await this.attachments.create({
                    workspace_id: project.workspace_id,
                    parent_type: AttachmentParentType.Project,
                    parent_id: projectId,
                    child_type: AttachmentChildType.File,
                    child_id: fileId,
                })
            }

            // Only create project revision attachments if we created a project revision
            if (newProjectRevisionId) {
                const promises: Promise<any>[] = [];
                // Keep track of file revisions we've already attached to avoid duplicates
                const attachedFileRevisionIds = new Set<string>();

                const previousProjectRevision = existingProjectRevisions[0] as typeof existingProjectRevisions[number] | undefined;

                if (previousProjectRevision) {
                    const fileRevisionsOfPreviousProjectRevision = await db.selectFrom('Attachment')
                        .innerJoin('FileRevision', 'Attachment.child_id', 'FileRevision.id')
                        .where('Attachment.parent_id', '=', previousProjectRevision.id)
                        .where('Attachment.child_type', '=', AttachmentChildType.FileRevision)
                        .where('Attachment.parent_type', '=', AttachmentParentType.ProjectRevision)
                        .select('FileRevision.id')
                        .execute();

                    for (const revision of fileRevisionsOfPreviousProjectRevision) {
                        // Skip if we've already attached this file revision
                        if (attachedFileRevisionIds.has(revision.id)) continue;
                        attachedFileRevisionIds.add(revision.id);

                        promises.push(this.attachments.create({
                            workspace_id: project.workspace_id,
                            parent_type: AttachmentParentType.ProjectRevision,
                            parent_id: newProjectRevisionId,
                            child_type: AttachmentChildType.FileRevision,
                            child_id: revision.id,
                        }));
                    }
                }

                // Attach the latest revision of the file being added
                if (existingFileRevisions.length > 0) {
                    const latestRevision = existingFileRevisions[0]!;

                    // Skip if we've already attached this file revision
                    if (!attachedFileRevisionIds.has(latestRevision.id)) {
                        attachedFileRevisionIds.add(latestRevision.id);

                        promises.push(this.attachments.create({
                            workspace_id: project.workspace_id,
                            parent_type: AttachmentParentType.ProjectRevision,
                            parent_id: newProjectRevisionId,
                            child_type: AttachmentChildType.FileRevision,
                            child_id: latestRevision.id,
                        }));
                    }
                }

                // Also attach the latest revision of all other files in the project
                const projectFiles = await db.selectFrom('File')
                    .innerJoin('Attachment', 'File.id', 'Attachment.child_id')
                    .where('Attachment.parent_type', '=', AttachmentParentType.Project)
                    .where('Attachment.parent_id', '=', projectId)
                    .where('Attachment.child_type', '=', AttachmentChildType.File)
                    .where('File.id', '!=', fileId) // Exclude the file we're currently adding
                    .select('File.id')
                    .execute();

                for (const projectFile of projectFiles) {
                    const latestFileRevision = await db.selectFrom('FileRevision')
                        .where('file_id', '=', projectFile.id)
                        .orderBy('revision_num', 'desc')
                        .selectAll()
                        .executeTakeFirst();

                    if (latestFileRevision && !attachedFileRevisionIds.has(latestFileRevision.id)) {
                        attachedFileRevisionIds.add(latestFileRevision.id);

                        promises.push(this.attachments.create({
                            workspace_id: project.workspace_id,
                            parent_type: AttachmentParentType.ProjectRevision,
                            parent_id: newProjectRevisionId,
                            child_type: AttachmentChildType.FileRevision,
                            child_id: latestFileRevision.id,
                        }));
                    }
                }

                await Promise.all(promises);
            }

            // DAW file detection with prioritized bounce file detection
            // Check if bounce_file attribute already exists
            const existingBounceAttr = await db.selectFrom('Attribute')
                .selectAll()
                .where('parent_type', '=', AttributeParentType.Project)
                .where('parent_id', '=', projectId)
                .where('name', '=', AttributeName.bounce_file)
                .executeTakeFirst();

            const existingLauncherAttr = await db.selectFrom('Attribute')
                .selectAll()
                .where('parent_type', '=', AttributeParentType.Project)
                .where('parent_id', '=', projectId)
                .where('name', '=', AttributeName.launcher_file)
                .executeTakeFirst();

            // Only proceed if we don't have both attributes set
            if (!existingBounceAttr || !existingLauncherAttr) {
                // Get all files in the project
                const projectFiles = await db.selectFrom('File')
                    .innerJoin('Attachment', 'File.id', 'Attachment.child_id')
                    .selectAll('File')
                    .where('Attachment.parent_type', '=', AttachmentParentType.Project)
                    .where('Attachment.parent_id', '=', projectId)
                    .where('Attachment.child_type', '=', AttachmentChildType.File)
                    .execute();

                let bounceFile: string | undefined;
                let launcherFile: string | undefined;

                // Determine current file type
                if (isAudioFile(file.name)) {
                    bounceFile = file.relative_path;
                } else if (isDawProjectFile(file.name)) {
                    launcherFile = file.relative_path;
                }

                // If we don't have a bounce file yet, search for one using priority order
                if (!bounceFile && !existingBounceAttr) {
                    const foundBounceFile = findBounceFileWithPriority(project.name, projectFiles);
                    bounceFile = foundBounceFile?.relative_path;
                }

                // Find launcher file if we don't have one
                if (!launcherFile && !existingLauncherAttr) {
                    launcherFile = projectFiles.find(f => isDawProjectFile(f.name))?.relative_path;
                }

                // Set launcher_file attribute if not exists and we found a launcher
                if (!existingLauncherAttr && launcherFile) {
                    await db.insertInto('Attribute').values({
                        id: generateId(),
                        name: AttributeName.launcher_file,
                        value: launcherFile,
                        parent_type: AttributeParentType.Project,
                        parent_id: projectId,
                        workspace_id: project.workspace_id,
                        created_by: this.userId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                    }).execute();
                }

                // Set bounce_file attribute if not exists and we found a bounce
                if (!existingBounceAttr && bounceFile) {
                    await db.insertInto('Attribute').values({
                        id: generateId(),
                        name: AttributeName.bounce_file,
                        value: bounceFile,
                        parent_type: AttributeParentType.Project,
                        parent_id: projectId,
                        workspace_id: project.workspace_id,
                        created_by: this.userId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                    }).execute();
                }
            }
        },

        createRevision: async (fileId: string) => {
            const db = this.getDB();
            const newId = generateId();

            const existingRevisions = await db.selectFrom('FileRevision')
                .where('file_id', '=', fileId)
                .select('id')
                .execute();

            const version = existingRevisions.length + 1;
            const revisionName = `v${version}`;

            const workspaceId = await db.selectFrom('File')
                .select('workspace_id')
                .where('id', '=', fileId)
                .executeTakeFirstOrThrow();

            return db.insertInto('FileRevision').values({
                id: newId,
                file_id: fileId,
                created_by: this.userId,
                created_at: new Date().toISOString(),
                revision_name: revisionName,
                revision_num: version,
                workspace_id: workspaceId.workspace_id,
            }).execute().then(() =>
                db.selectFrom('FileRevision').where('id', '=', newId).selectAll().executeTakeFirstOrThrow()
            );
        },
    };

    projects = {
        get: async (projectId: string) => {
            const db = this.getDB();
            return await db.selectFrom('Project').where('id', '=', projectId).selectAll().executeTakeFirst();
        },
    }

    attachments = {
        get: async (attachmentId: string) => {
            const db = this.getDB();
            return await db.selectFrom('Attachment').where('id', '=', attachmentId).selectAll().executeTakeFirst();
        },

        create: async (args: {workspace_id: string, parent_type: AttachmentParentType, parent_id: string, child_type: AttachmentChildType, child_id: string}) => {
            const db = this.getDB();
            const newAttachmentId = generateId();
            const result = await db.insertInto('Attachment').values({
                id: newAttachmentId,
                child_id: args.child_id,
                child_type: args.child_type,
                parent_id: args.parent_id,
                parent_type: args.parent_type,
                created_by: this.userId,
                workspace_id: args.workspace_id,
                updated_at: new Date().toISOString(),
            }).execute().then(() =>
                db.selectFrom('Attachment').where('id', '=', newAttachmentId).selectAll().executeTakeFirst()
            );

            return result;
        },

    }

    workspaces = {
        create: async (userId: string) => {
            const db = this.getDB();
            db.selectFrom('WorkspaceMember')
                .innerJoin('Workspace', 'WorkspaceMember.workspace_id', 'Workspace.id')
                .selectAll()
                .where('WorkspaceMember.user_id', '=', userId)
                .execute();
        },
    };

    user_activity = {
        create: async (args: {workspace_id: string, activity_name: UserActivityName, object_type: UserActivityObjectType, object_id: string}) => {
            const db = this.getDB();
            const id = generateId();
            return db.insertInto('UserActivity')
                .values({
                    id: id,
                    activity_name: args.activity_name,
                    object_type: args.object_type,
                    object_id: args.object_id,
                    workspace_id: args.workspace_id,
                    created_by: this.userId,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                })
                .execute()
                .then(() => {
                    return db.selectFrom('UserActivity')
                        .where('id', '=', id)
                        .selectAll()
                        .executeTakeFirst()!;
                });
        },
    }

    stars = {
        create: async (args: {parent_type: StarParentType, parent_id: string, workspace_id: string}) => {
            const db = this.getDB();
            const id = generateId();
            return db.insertInto('Star')
                .values({
                    id: id,
                    parent_type: args.parent_type,
                    parent_id: args.parent_id,
                    workspace_id: args.workspace_id,
                    created_by: this.userId,
                })
                .execute()
                .then(() => {
                    return db.selectFrom('Star')
                        .where('id', '=', id)
                        .selectAll()
                        .executeTakeFirst()!;
                });
        },
        delete: async (args: {parent_type: StarParentType, parent_id: string}) => {
            const db = this.getDB();
            return db.deleteFrom('Star')
                .where('parent_type', '=', args.parent_type)
                .where('parent_id', '=', args.parent_id)
                .execute();
        },
    }

    user_preferences = {
        upsert: async (args: {user_id: string, key: UserPreferenceType, value: string, workspace_id?: string}) => {
            const db = this.getDB();
            const existingPreference = await db.selectFrom('UserPreference')
                .where('user_id', '=', args.user_id)
                .where('key', '=', args.key)
                .where('workspace_id', args.workspace_id ? '=' : 'is', args.workspace_id || null)
                .selectAll()
                .executeTakeFirst();

            if (existingPreference) {
                return db.updateTable('UserPreference')
                    .set({
                        value: args.value,
                        updated_at: new Date().toISOString(),
                    })
                    .where('id', '=', existingPreference.id)
                    .execute()
                    .then(() => {
                        return db.selectFrom('UserPreference')
                            .where('id', '=', existingPreference.id)
                            .selectAll()
                            .executeTakeFirstOrThrow();
                    });
            } else {
                const newId = generateId();
                return db.insertInto('UserPreference')
                    .values({
                        id: newId,
                        user_id: args.user_id,
                        key: args.key,
                        value: args.value,
                        workspace_id: args.workspace_id,
                        created_by: this.userId,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString(),
                    })
                    .execute()
                    .then(() => {
                        return db.selectFrom('UserPreference')
                            .where('id', '=', newId)
                            .selectAll()
                            .executeTakeFirstOrThrow();
                    });
            }
        },
        get: async (args: {user_id: string, key: UserPreferenceType, workspace_id?: string}) => {
            const db = this.getDB();
            return db.selectFrom('UserPreference')
                .where('user_id', '=', args.user_id)
                .where('key', '=', args.key)
                .where('workspace_id', args.workspace_id ? '=' : 'is', args.workspace_id || null)
                .selectAll()
                .executeTakeFirst();
        },
        delete: async (args: {user_id: string, key: UserPreferenceType, workspace_id?: string}) => {
            const db = this.getDB();
            return db.deleteFrom('UserPreference')
                .where('user_id', '=', args.user_id)
                .where('key', '=', args.key)
                .where('workspace_id', args.workspace_id ? '=' : 'is', args.workspace_id || null)
                .execute();
        },
    }
}

export function createZodAction<T extends ZodType, R>(
    schema: T,
    callback: (obj: z.infer<T>) => R
): (input: z.infer<T>, workspaceContext: WorkspaceContext) => R {
    return (input: z.infer<T>) => {
        try {
            const parsed = schema.parse(input);
            return callback(parsed);
        } catch (error) {
            if (error instanceof ZodError) {
                throw new Error("Validation failed: " + error.message);
            } else {
                throw error;
            }
        }
    };
}

const getSystemType = () => {
    const systemTypes = [
        

        'local',
    ] as const;

    return systemTypes[0] as typeof systemTypes[number];
}
